import React, { FC, useCallback, memo, useMemo } from 'react'
import { Button, Table } from '@wmgtech/legato'
import { useForm } from 'react-hook-form'
import { ColumnsType } from '@wmgtech/legato/build/components/Table/types'
import { AlterAutocomplete } from './AlterAutoComplete'

import { useFreezeModal } from 'shared/hooks'

import { IAlterNamesTable } from './AlterNamesTable.props'

export const AlterNamesTable: FC<IAlterNamesTable> = memo(
  ({ productId, alterNamesData, onDeleteAlterName, onCreateAlterName }) => {
    useFreezeModal()

    const deleteAlterName = useCallback(
      (result) => () => {
        onDeleteAlterName(result.id)
      },
      [onDeleteAlterName]
    )

    const { control, watch, setValue, getValues, reset } = useForm<any>({
      defaultValues: { isAdding: false, artist_name: '', product_name: '' }
    })

    const isAdding = watch('isAdding')
    const isArtistName = watch('artist_name')
    const isProductName = watch('product_name')

    const alterNames = useMemo(() => (isAdding ? [...alterNamesData, {}] : alterNamesData), [alterNamesData, isAdding])

    const onAddNewAlterName = useCallback(() => {
      setValue('isAdding', true)
    }, [setValue])

    const onCloseForm = useCallback(() => {
      setValue('isAdding', false)
      reset()
    }, [reset, setValue])

    const onSaveForm = useCallback(async () => {
      const { artist_name, product_name } = getValues()
      if (artist_name || product_name) {
        await onCreateAlterName({ artist_name, product_name, product_id: productId })
        onCloseForm()
      }
    }, [onCreateAlterName, getValues, onCloseForm, productId])

    const onSelect = useCallback(
      (type: string, value: string) => {
        setValue(type, value)
      },
      [setValue]
    )

    const onRow = useCallback(
      (_, idx) => {
        if (isAdding && idx === alterNames.length - 1) {
          return { 'data-edited': 'true', id: idx.toString() }
        }
        return { id: idx.toString() }
      },
      [isAdding, alterNames.length]
    )

    const columns = useMemo<ColumnsType<unknown>[]>(
      () => [
        {
          dataIndex: 'artist_name',
          key: 'artist_name',
          title: 'Artist',
          width: '45%',
          sortable: false,
          render: (value, record: any) =>
            record?.id ? value : (
              <AlterAutocomplete
                placeholder="Artist Name"
                fieldName="artist_name"
                disabled={!!isProductName}
                control={control}
                onSelect={onSelect}
              />
            )
        },
        {
          dataIndex: 'product_name',
          key: 'product_name',
          title: 'Product',
          width: '40%',
          sortable: false,
          render: (value, record: any) =>
            record?.id ? value : (
              <AlterAutocomplete
                placeholder="Product Name"
                fieldName="product_name"
                disabled={!!isArtistName}
                control={control}
                onSelect={onSelect}
              />
            )
        },
        {
          dataIndex: 'status',
          key: 'status',
          title: '',
          sortable: false,
          render: (_, result: any) =>
            result?.id ? (
              <Button
                onClick={deleteAlterName(result)}
                icon="trash-can"
                containerStyle="link"
                colorType="secondary-black"
              />
            ) : (
              <div className="alter-names-modal__table-actions">
                <Button onClick={onCloseForm} icon="xmark" containerStyle="link" colorType="secondary-black" />
                <Button onClick={onSaveForm} icon="check" containerStyle="link" colorType="secondary-black" />
              </div>
            )
        }
      ],
      [onCloseForm, onSaveForm, deleteAlterName, control, onSelect, isArtistName, isProductName]
    )

    return (
      <>
        {
          !!alterNames.length && (
            <Table
              className="alter-names-modal__table"
              rowKey={(record: any) => record.id}
              columns={columns}
              data={alterNames}
              pagination={false}
              onRow={onRow}
              condensed
              sticky
            />
          )
        }
        <Button
          disabled={isAdding}
          onClick={onAddNewAlterName}
          label="Add New"
          secondaryIcon="circle-plus"
          colorType="secondary-black"
          containerStyle="outline"
          className="alter-names-modal__add-new"
        />
      </>
    )
  }
)
